
/** @typedef {Object} Debater
 * @property {string} nick - nickname of the debater
 * @property {string} name - name
 * @property {string} state - current state of the debater
 * @property {string} avatarUrl - valid URL to avatar
 */

/** @typedef {Object} Chat
 * @property {string} id - unique ID of the chat (UUID)
 * @property {string} [avatarUrl] - Recommended avatar from server
 * @property {boolean} closed - true if this chat is already closed and cannot accept messages
 * @property {string} [topic] - short title
 * @property {string} [subtitle] - short sub-title
 * @property {Debater[]} debaters - list of participants in this chat
 * @property {number} unseen - number of unseen messages in this chat
 */

import {ChatUI} from "./ChatUI.js";
import {chatClientState} from "./ChatClientState.js";
import typingDots from "./TypingDots.js";
import {msg} from "@/i18n/Message.ts";

const AVATAR_SHIFT = 20;

/**
 * Controller of chat list
 */

export class Chats extends ChatUI {
	/**
	 * @param {ChatClient} client
	 * @param {jQuery} header
	 * @param {jQuery} main - element which will be container of the widget
	 * @param {jQuery} footer
	 * @param {string} me - nick of the current user
	 */
	constructor(client, header, main, footer, me) {
		super();
		this._editMode = false;
		this.client = client;
		this.header = header;
		this.container = main;
		this.footer = footer;
		this.me = me;
		this.chats = [];	// list of chats
		this.install();
	}

	get editMode() {
		return this._editMode;
	}

	set editMode(value) {
		this._editMode = value;
		if (this.chatListContainer) {
			if (this._editMode) this.chatListContainer.addClass("edit-mode");
			else this.chatListContainer.removeClass("edit-mode");
			this.setEditButtonLabel();
		}
	}

	install() {
		const icn = $(Icon.fi("chat"));
		this.header.append(icn);

		const title = $("<div class='chat-header-title'></div>");
		title.text(bundle.get("chats.title"));
		this.header.append(title);

		this.chatListContainer = $("<div class='chat-list'></div>");
		if (this._editMode) this.chatListContainer.addClass("edit-mode");
		this.container.append(this.chatListContainer);

		this.chatListControls =$("<div class='chat-controls'></div>");
		this.footer.append(this.chatListControls);
		const r = $("<div class='chat-controls-row'></div>");
		this.chatListControls.append(r);

		const plusIcn = $(Icon.fi("plus-circle-16-full", "link-like-fi"));
		r.append(plusIcn);

		this.editButton = $("<button class='fbutton'></button>");
		this.setEditButtonLabel();
		r.append(this.editButton);

		plusIcn.on("click", this.newChat.bind(this));
		this.editButton.on("click", this.toggleEditMode.bind(this));

		this.reload();
	}

	toggleEditMode() {
		this.editMode = !this.editMode;
	}

	setEditButtonLabel() {
		if (this.editButton) {
			if (this.editMode) this.editButton.text(msg("bas.cancel"));
			else this.editButton.text(msg("menu.editIt"));
		}
	}

	newChat() {
		this.fireEvent("newchat", {});
	}

	reload(force = false) {
		const d = chatClientState.data;
		if (d == null || force) {
			$.getJSON("api/messaging/chats")
				.done((data) => {
					this.displayChats(data);
					chatClientState.data = data;
				});
		} else {
			this.displayChats(d);
		}
	}

	displayChats(data) {
		this.chats = data;
		this.chatListContainer.empty();
		let totalUnseen = 0;
		if (this.chats.length === 0) {
			const warn = $("<div class='chat-empty'></div>");
			warn.text(bundle.get("chat.none"));
			this.chatListContainer.append(warn);
			if (this.editButton) this.editButton.prop("disabled", true);
			this.editMode = false;
		} else {
			for (let ch of this.chats) {
				const name = this.nameFromChatObj(ch);
				const subName = this.subNameFromChatObj(ch);
				const debaters = this.debatersFromChatObj(ch);
				const unseen = ch.unseen ? ch.unseen : 0;

				const debContainer = Chats.chatListItem(name, subName, debaters, unseen);
				debContainer.attr("data-chat", ch.id);
				const leaveButton = $(Icon.fi("minus-circle-16-full", "link-like-fi edit-only-leave"));
				debContainer.prepend(leaveButton);

				this.chatListContainer.append(debContainer);

				debContainer.on("click", this.chatSelected.bind(this, ch.id));
				leaveButton.on("click", this.leaveChat.bind(this, ch.id));
				chatClientState.setUnseen(ch.id, unseen);	// total is updated later
				totalUnseen += unseen;
			}
			if (this.editButton) this.editButton.prop("disabled", false);
		}
		this.client.unseen = totalUnseen;
	}

	static chatListItem(name, subName, debaters, unseen) {
		const debContainer = $("<div class='chat-list-item chat-debater'></div>");
		const debAvatarContainer = $("<div class='debater-avatar'></div>");
		let top = 0, left = 0, z = 1000, idx = 0;
		let maxTop = 0, maxLeft = 0;
		for (let d of debaters) {
			top = Math.floor(idx / 4) * AVATAR_SHIFT;
			left = (idx % 4) * AVATAR_SHIFT;
			maxTop = Math.max(maxTop, top);
			maxLeft = Math.max(maxLeft, left);

			const debAvatar = $("<img class='debater-avatar-img'>");
			debAvatar.attr("src", d.avatarUrl);
			debAvatar.attr("alt", d.name);
			if (top > 0) debAvatar.css("top", top + "px");
			if (left > 0) debAvatar.css("left", left + "px");
			debAvatar.css("z-index", z);
			debAvatar.attr("data-avatar", d.nick);
			debAvatar.addClass("chat-state-" + d.state);
			z--;
			debAvatarContainer.append(debAvatar);
			idx++;
		}
		debAvatarContainer.css("width", (maxLeft + 34) + "px");
		debAvatarContainer.css("height", (maxTop + 34) + "px");
		debContainer.append(debAvatarContainer);

		const debInfoContainer = $("<div class='debater-info'></div>");
		const debInfoName = $("<div class='debater-info-name'></div>");
		const debInfoNick = $("<div class='debater-info-nick'></div>");
		debInfoName.text(name);
		debInfoNick.text(subName);
		debInfoContainer.append(debInfoName);
		debInfoName.append(typingDots(true));
		debInfoContainer.append(debInfoNick);
		debContainer.append(debInfoContainer);

		const badge = $("<div class='chat-unseen'>"+unseen+"</div>");
		debContainer.append(badge);
		if (unseen <= 0) badge.hide();

		return debContainer;
	}

	chatSelected(id) {
		if (!this._editMode) {
			this.fireEvent("chatselect", { chat: id });
		}
	}

	leaveChat(id) {
		if (this._editMode) {
			$.getJSON("api/messaging/chats/"+id+"/leave")
				.done((data) => {
					this.displayChats(data);
					chatClientState.data = data;
				});
		}
	}

	/**
	 * @param {Chat} ch
	 * @return {string}
	 */
	nameFromChatObj(ch) {
		if (ch.topic) return ch.topic;
		for (let d of ch.debaters) {
			if (this.me !== d.nick) return d.name;
		}
		return "-";
	}

	/**
	 * @param {Chat} ch
	 * @return {string}
	 */
	stateFromChatObj(ch) {
		const states = [];
		for (let d of ch.debaters) {
			if (this.me !== d.nick) {
				states.push(d.state);
			}
		}
		return this.pickRepresentativeState(states);
	}

	/**
	 * @param {string[]} states
	 * @return {string}
	 */
	pickRepresentativeState(states) {
		let s = "OFFLINE";
		for (let st of states) {
			if (Chats.stateValue(s) < Chats.stateValue(st)) s = st;
		}
		return s;
	}

	/**
	 * @param {string} s
	 * @return {number}
	 */
	static stateValue(s) {
		switch (s) {
			case "ONLINE":
				return 5;
			case "OFFLINE":
				return 1;
			case "AWAY":
				return 4;
			case "DO_NOT_DISTURB":
				return 3;
			case "HIDDEN":
				return 2;
			case "UNAVAILABLE":
			default:
				return 0;
		}
	}

	/**
	 * @param {Chat} ch
	 * @return {Debater[]}
	 */
	debatersFromChatObj(ch) {
		const avs = [];
		for (let d of ch.debaters) {
			if (this.me !== d.nick) avs.push(d);
		}
		return avs;
	}

	/**
	 * @param {Chat} ch
	 * @return {string}
	 */
	subNameFromChatObj(ch) {
		if (ch.subtitle) return ch.subtitle;
		const nicks = [];
		for (let d of ch.debaters) {
			if (this.me !== d.nick) nicks.push("@"+d.nick);
		}
		return nicks.join(", ");
	}

	newMessageInChat(chatId, msg) {
		super.newMessageInChat(chatId, msg);
		this.typingEnded(chatId, {from:msg.from});
		this.chatListContainer.find("[data-chat=" + chatId + "]").find(".chat-unseen").each(function(idx) {
			const badge = $(this);
			let currentCount = parseInt(badge.text());
			badge.text((currentCount+1));
			badge.show();
		});
		chatClientState.setUnseen(chatId, chatClientState.getUnseen(chatId) + 1);	// increment per-chat count
		this.client.unseen++;															// increment total count
	}

	debaterStateChanged(handle, state) {
		super.debaterStateChanged(handle, state);
		// Update data structure
		let wasChange = false;
		for (let ch of this.chats) {
			for (let db of ch.debaters) {
				if (db.nick === handle) {
					if (db.state !== state) {
						db.state = state;
						wasChange = true;
					}
					break;
				}
			}
		}
		if (wasChange) {
			Chats.setStateClass(this.chatListContainer, handle, state);
			chatClientState.data = this.chats;
		}
	}

	static setStateClass(rootElm, handle, state) {
		rootElm.find("[data-avatar=" + handle + "]")
			.removeClass("chat-state-ONLINE")
			.removeClass("chat-state-OFFLINE")
			.removeClass("chat-state-AWAY")
			.removeClass("chat-state-DO_NOT_DISTURB")
			.removeClass("chat-state-HIDDEN")
			.removeClass("chat-state-UNAVAILABLE")
			.addClass("chat-state-"+state);
	}

	typingStarted(chatId, data) {
		super.typingStarted(chatId, data);
		this.chatListContainer.find("[data-chat=" + chatId + "]").find(".chat-dots").css("visibility", "visible");
	}

	typingEnded(chatId, data) {
		super.typingEnded(chatId, data);
		this.chatListContainer.find("[data-chat=" + chatId + "]").find(".chat-dots").css("visibility", "hidden");
	}
}
